import memoize from 'memoize-one';
import regression from 'regression';

import {produce} from 'immer';
import {ChartAggregationCalcType} from 'views/storyboard/utils/types';
import {getChartDatapointTotals} from 'views/storyboard/utils/calculations/chartDatapointTotals';
import {getChartDatapointMeans} from 'views/storyboard/utils/calculations/chartDatapointMeans';
import {BarChartData} from 'views/storyboard/charts/bar/BarChartWrapper';
import {LineChartData} from 'views/storyboard/charts/line/LineChartWrapper';
import {AreaChartData} from 'views/storyboard/charts/area/AreaChartWrapper';

export type LinearRegressionChartData = (
    AreaChartData | BarChartData | LineChartData
);

const getChartLinearRegression = <T extends LinearRegressionChartData>(
    data: T[],
    dataKeys: string[],
    calcType: ChartAggregationCalcType,
    excludedKeys: string[] = [],
) => {
    return memoize(
        // Calculates the total for every key for every data point and adds it together
        (
            data: T[],
            dataKeys: string[],
            calcType: ChartAggregationCalcType,
            excludedKeys: string[] = []
        ) => {
            let aggregatedData;
            if (calcType === ChartAggregationCalcType.Cumulative) {
                aggregatedData = getChartDatapointTotals(data, dataKeys, excludedKeys);
            } else if (calcType === ChartAggregationCalcType.Average) {
                aggregatedData = getChartDatapointMeans(data, dataKeys, excludedKeys);
            } else {
                throw Error('calcType not specified');
            }

            return regression.linear(
                aggregatedData.map((
                    el: number,
                    idx: number,
                ) => [idx, el])
            );
        },
    )(data, dataKeys, calcType, excludedKeys);
};

export const addLinearRegressionToData = <T extends LinearRegressionChartData>(
    data: T[],
    dataKeys: string[],
    calcType: ChartAggregationCalcType,
    excludedKeys: string[] = [],
): T[] => {
    const linearRegEquation = getChartLinearRegression(data, dataKeys, calcType, excludedKeys).equation;

    return produce(data, (draft) => {
        if (draft.length > 0) {
            // @ts-ignore
            draft[0].linearReg = linearRegEquation[1];
            // @ts-ignore
            draft[draft.length - 1].linearReg = linearRegEquation[0] * (draft.length - 1) + linearRegEquation[1];
        }
    });
};
